// src/pages/buyer/SupportPage.jsx
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ticketsApi } from '@/api/services'
import toast from 'react-hot-toast'
import { HelpCircle, MessageSquare, Plus, Send, ChevronLeft } from 'lucide-react'

const STATUS_COLORS = {
  open:           'bg-blue-100 text-blue-700',
  in_progress:    'bg-amber-100 text-amber-700',
  awaiting_reply: 'bg-purple-100 text-purple-700',
  resolved:       'bg-green-100 text-green-700',
  closed:         'bg-gray-100 text-gray-600',
}

const PRIORITY_COLORS = {
  low:    'text-gray-500',
  medium: 'text-blue-500',
  high:   'text-amber-500',
  urgent: 'text-red-500',
}

const CATEGORIES = [
  { value: 'order_issue', label: 'Order Issue' },
  { value: 'payment', label: 'Payment' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'product_quality', label: 'Product Quality' },
  { value: 'return_exchange', label: 'Return/Exchange' },
  { value: 'account', label: 'Account' },
  { value: 'technical', label: 'Technical' },
  { value: 'other', label: 'Other' },
]

export default function SupportPage() {
  const qc = useQueryClient()
  const [view, setView] = useState('list')      // list | detail | new
  const [selectedId, setSelectedId] = useState(null)
  const [replyText, setReplyText] = useState('')
  const [form, setForm] = useState({ category: '', subject: '', message: '', priority: 'medium' })

  const { data: listData, isLoading } = useQuery({
    queryKey: ['buyer-tickets'],
    queryFn: () => ticketsApi.list().then(r => r.data),
  })

  const { data: detailData } = useQuery({
    queryKey: ['buyer-ticket', selectedId],
    queryFn: () => ticketsApi.show(selectedId).then(r => r.data),
    enabled: !!selectedId && view === 'detail',
  })

  const createMut = useMutation({
    mutationFn: (d) => ticketsApi.create(d),
    onSuccess: () => {
      toast.success('Ticket created')
      setView('list')
      setForm({ category: '', subject: '', message: '', priority: 'medium' })
      qc.invalidateQueries({ queryKey: ['buyer-tickets'] })
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to create ticket'),
  })

  const replyMut = useMutation({
    mutationFn: ({ id, body }) => ticketsApi.reply(id, { body }),
    onSuccess: () => {
      setReplyText('')
      qc.invalidateQueries({ queryKey: ['buyer-ticket', selectedId] })
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Reply failed'),
  })

  const tickets = listData?.data ?? []
  const ticket = detailData?.data ?? null

  if (view === 'new') {
    return (
      <div className="space-y-6">
        <button onClick={() => setView('list')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ChevronLeft className="w-4 h-4" /> Back to tickets
        </button>
        <h2 className="text-xl font-bold text-gray-900">New Support Ticket</h2>
        <div className="bg-white rounded-lg border p-5 space-y-4 max-w-2xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select...</option>
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
            <input
              value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="Brief summary of your issue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              value={form.message}
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              rows={5}
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="Describe your issue in detail..."
            />
          </div>
          <button
            onClick={() => createMut.mutate(form)}
            disabled={!form.category || !form.subject || !form.message || createMut.isPending}
            className="bg-pink-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-pink-600 disabled:opacity-50"
          >
            Create Ticket
          </button>
        </div>
      </div>
    )
  }

  if (view === 'detail' && ticket) {
    return (
      <div className="space-y-4">
        <button onClick={() => { setView('list'); setSelectedId(null) }} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ChevronLeft className="w-4 h-4" /> Back to tickets
        </button>
        <div className="bg-white rounded-lg border p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">{ticket.subject}</h2>
              <div className="text-xs text-gray-500 mt-1">
                {ticket.ticket_number} &middot; {CATEGORIES.find(c => c.value === ticket.category)?.label}
                {ticket.order && <> &middot; Order #{ticket.order.order_number}</>}
              </div>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ticket.status]}`}>
              {ticket.status?.replace(/_/g, ' ').toUpperCase()}
            </span>
          </div>

          {/* Messages */}
          <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
            {(ticket.messages || []).map(msg => (
              <div key={msg.id} className={`p-3 rounded-lg text-sm ${msg.user?.role === 'admin' ? 'bg-blue-50 ml-4' : 'bg-gray-50 mr-4'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-800">{msg.user?.name}</span>
                  <span className="text-xs text-gray-400">{new Date(msg.created_at).toLocaleString('en-IN')}</span>
                </div>
                <p className="text-gray-700 whitespace-pre-wrap">{msg.body}</p>
              </div>
            ))}
          </div>

          {/* Reply */}
          {!['resolved', 'closed'].includes(ticket.status) && (
            <div className="flex gap-2">
              <input
                value={replyText}
                onChange={e => setReplyText(e.target.value)}
                placeholder="Type your reply..."
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && replyText.trim() && replyMut.mutate({ id: ticket.id, body: replyText })}
              />
              <button
                onClick={() => replyMut.mutate({ id: ticket.id, body: replyText })}
                disabled={!replyText.trim() || replyMut.isPending}
                className="bg-pink-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-pink-600 disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Support</h1>
        <button
          onClick={() => setView('new')}
          className="bg-pink-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-pink-600 flex items-center gap-1"
        >
          <Plus className="w-4 h-4" /> New Ticket
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : tickets.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <HelpCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No support tickets</p>
          <p className="text-xs mt-1">Create a ticket if you need help with an order or your account</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tickets.map(t => (
            <button
              key={t.id}
              onClick={() => { setSelectedId(t.id); setView('detail') }}
              className="w-full bg-white rounded-lg border p-4 flex items-center justify-between hover:shadow-sm transition text-left"
            >
              <div className="flex items-center gap-3">
                <MessageSquare className={`w-5 h-5 ${PRIORITY_COLORS[t.priority]}`} />
                <div>
                  <div className="font-medium text-gray-900 text-sm">{t.subject}</div>
                  <div className="text-xs text-gray-500">
                    {t.ticket_number} &middot; {CATEGORIES.find(c => c.value === t.category)?.label}
                    {t.messages_count ? ` &middot; ${t.messages_count} messages` : ''}
                  </div>
                </div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[t.status]}`}>
                {t.status?.replace(/_/g, ' ').toUpperCase()}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
